// src/components/agents/agent-constants.ts
//
// Constantes compartilhadas da tela de Agentes (Reforma UX §6.6, AGT-01..06).
// Labels de archetype, badges de status e helpers de status ativo/inativo.

import type { AgentArchetype, AgentListItem } from '@/lib/api';

export const ARCHETYPE_LABELS: Record<
  AgentArchetype,
  { label: string; badge: 'brand' | 'info' | 'default' }
> = {
  companion: { label: 'companion', badge: 'brand' },
  domain_worker: { label: 'worker', badge: 'info' },
  system: { label: 'system', badge: 'default' },
  ephemeral: { label: 'ephemeral', badge: 'default' },
};

export const STATUS_BADGE: Record<
  string,
  'default' | 'info' | 'success' | 'warning' | 'danger'
> = {
  draft: 'default',
  active: 'success',
  published: 'success',
  paused: 'warning',
  inactive: 'warning',
  archived: 'danger',
};

/**
 * Status "ativo" do switch. Backend retorna `active`/`published` pra agents e
 * `published`/`inactive` pra companions (is_active mapeado em GET /v1/agents).
 */
export function isAgentActive(status: string): boolean {
  return status === 'active' || status === 'published';
}

/** Iniciais pro fallback do avatar (mesmo padrão do BusinessLogo). */
export function agentInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Toggle de status: agent real usa PATCH /v1/agents/:slug { status } (suportado —
 * VALID_STATUS no backend inclui active/paused); companion usa
 * PATCH /v1/companions/:id { is_active } (PATCH /v1/agents descarta status pra
 * companion_profiles).
 */
export function isCompanionProfile(agent: Pick<AgentListItem, 'entity_type'>): boolean {
  return agent.entity_type === 'companion_profile';
}

export interface ModelPolicyValue {
  provider?: string;
  model_key?: string;
  temperature?: number;
  /** Contrato v2: pares explícitos e ordenados; nunca inferir provider pela key. */
  fallback_models?: ModelPolicyPair[];
  [key: string]: unknown;
}

export interface ModelPolicyPair {
  provider: string;
  model_key: string;
}

interface RoutePolicyV2 {
  version: 2;
  enabled: boolean;
  provider_order: string[];
  models_by_provider: Record<string, string[]>;
  allow_cross_provider: boolean;
  retry: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function parseFallbackPairs(value: unknown): ModelPolicyPair[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.flatMap((candidate) => {
    if (!isRecord(candidate)) return [];
    return typeof candidate.provider === 'string' && typeof candidate.model_key === 'string'
      ? [{ provider: candidate.provider, model_key: candidate.model_key }]
      : [];
  });
}

function parseRoutePolicyV2(value: unknown): RoutePolicyV2 | undefined {
  if (!isRecord(value) || value.version !== 2) return undefined;
  if (
    typeof value.enabled !== 'boolean'
    || typeof value.allow_cross_provider !== 'boolean'
    || !Array.isArray(value.provider_order)
    || !value.provider_order.every((provider) => typeof provider === 'string')
    || !isRecord(value.models_by_provider)
  ) {
    return undefined;
  }

  const modelsByProvider: Record<string, string[]> = {};
  for (const [provider, models] of Object.entries(value.models_by_provider)) {
    if (!Array.isArray(models) || !models.every((model) => typeof model === 'string')) {
      return undefined;
    }
    modelsByProvider[provider] = [...models];
  }

  return {
    version: 2,
    enabled: value.enabled,
    provider_order: [...value.provider_order] as string[],
    models_by_provider: modelsByProvider,
    allow_cross_provider: value.allow_cross_provider,
    retry: value.retry,
  };
}

function routePolicyFallbacks(
  routePolicy: RoutePolicyV2 | undefined,
  provider: string | undefined,
  modelKey: string | undefined,
): ModelPolicyPair[] | undefined {
  if (!routePolicy || !provider || !modelKey) return undefined;
  const aliases = routePolicy.provider_order.flatMap((routeProvider) =>
    (routePolicy.models_by_provider[routeProvider] ?? []).map((routeModel) => ({
      provider: routeProvider,
      model_key: routeModel,
    })),
  );
  const first = aliases[0];
  if (first?.provider !== provider || first.model_key !== modelKey) return undefined;
  return aliases.slice(1);
}

/** Extrai provider/model/temperature de um model_policy jsonb (shape livre). */
export function parseModelPolicy(policy: unknown): ModelPolicyValue {
  if (!isRecord(policy)) return {};
  const p = policy;
  const provider = typeof p.provider === 'string' ? p.provider : undefined;
  const model_key =
    typeof p.model_key === 'string'
      ? p.model_key
      : typeof p.model === 'string'
        ? p.model
        : typeof p.default === 'string'
          ? p.default
          : undefined;
  const temperature = typeof p.temperature === 'number' ? p.temperature : undefined;
  const explicitFallbacks = parseFallbackPairs(p.fallback_models);
  const fallback_models = explicitFallbacks
    ?? routePolicyFallbacks(parseRoutePolicyV2(p.route_policy), provider, model_key);
  const parsed: ModelPolicyValue = { ...p, provider, model_key, temperature };
  if (fallback_models) {
    parsed.fallback_models = fallback_models;
  } else {
    // Não propaga uma forma inválida para o PATCH; policies legacy sem o campo
    // continuam iguais porque não entram nesta ramificação.
    delete parsed.fallback_models;
  }
  return parsed;
}

/** Ordem persistível: cada provider ocupa um bloco único e o primário abre a cadeia. */
export function isValidModelFallbackOrder(
  primaryProvider: string,
  fallbacks: readonly ModelPolicyPair[],
): boolean {
  const exhaustedProviders = new Set<string>();
  let currentProvider = primaryProvider;

  for (const fallback of fallbacks) {
    if (fallback.provider === currentProvider) continue;
    exhaustedProviders.add(currentProvider);
    if (exhaustedProviders.has(fallback.provider)) return false;
    currentProvider = fallback.provider;
  }
  return true;
}

/** Remove repetidos/primário e agrupa providers sem alterar a ordem interna. */
export function normalizeModelFallbacks(
  primary: ModelPolicyPair,
  fallbacks: readonly ModelPolicyPair[],
): ModelPolicyPair[] {
  const seenPairs = new Set<string>();
  const providerOrder: string[] = [];
  const byProvider = new Map<string, ModelPolicyPair[]>();

  for (const fallback of fallbacks) {
    const pairKey = `${fallback.provider}:${fallback.model_key}`;
    if (
      pairKey === `${primary.provider}:${primary.model_key}`
      || seenPairs.has(pairKey)
    ) {
      continue;
    }
    seenPairs.add(pairKey);
    if (!byProvider.has(fallback.provider)) {
      byProvider.set(fallback.provider, []);
      providerOrder.push(fallback.provider);
    }
    byProvider.get(fallback.provider)?.push({ ...fallback });
  }

  const orderedProviders = [
    ...(byProvider.has(primary.provider) ? [primary.provider] : []),
    ...providerOrder.filter((provider) => provider !== primary.provider),
  ];
  return orderedProviders.flatMap((provider) => byProvider.get(provider) ?? []);
}

/**
 * Mantém as duas representações aprovadas pelo backend em round-trip exato:
 * campos top-level + alias route_policy v2. Opções de execução permanecem
 * iguais, exceto pinning incompatível quando o usuário declara cross-provider.
 */
export function synchronizeModelPolicy(
  current: ModelPolicyValue,
  primary: ModelPolicyPair,
  fallbacks: readonly ModelPolicyPair[],
  options: { forceFallbackModels?: boolean } = {},
): ModelPolicyValue {
  const normalizedFallbacks = normalizeModelFallbacks(primary, fallbacks);
  const routePolicy = parseRoutePolicyV2(current.route_policy);
  const shouldPersistFallbacks = options.forceFallbackModels
    || Array.isArray(current.fallback_models)
    || Boolean(routePolicy)
    || normalizedFallbacks.length > 0;
  const next: ModelPolicyValue = {
    ...current,
    provider: primary.provider,
    model_key: primary.model_key,
  };

  if (shouldPersistFallbacks) {
    next.fallback_models = normalizedFallbacks;
  } else {
    delete next.fallback_models;
  }

  if (routePolicy) {
    const declaredPairs = [primary, ...normalizedFallbacks];
    const providerOrder = [...new Set(declaredPairs.map((pair) => pair.provider))];
    const modelsByProvider = Object.fromEntries(providerOrder.map((provider) => [
      provider,
      declaredPairs
        .filter((pair) => pair.provider === provider)
        .map((pair) => pair.model_key),
    ]));
    const crossProvider = normalizedFallbacks.some(
      (fallback) => fallback.provider !== primary.provider,
    );
    next.route_policy = {
      version: 2,
      enabled: crossProvider ? true : routePolicy.enabled,
      provider_order: providerOrder,
      models_by_provider: modelsByProvider,
      allow_cross_provider: crossProvider ? true : routePolicy.allow_cross_provider,
      retry: routePolicy.retry,
    };
  }

  return next;
}
